import formStyles from '@he-novation/design-system/styles/form-styles.module.css';
import { useEffect, useState } from 'react';
import { Client, type DetailedWorkspace } from '@he-novation/config/types/client.types';
import { WorkspaceFeature } from '@he-novation/config/types/workspace.types';
import { ButtonTone } from '@he-novation/design-system/components/buttons/Button/Button';
import {
    FormFields,
    ModularForm
} from '@he-novation/design-system/components/form/FormField/FormField';
import { Error } from '@he-novation/design-system/components/text/Error/Error';
import { Loader } from '@he-novation/design-system/components/widgets/Loader/Loader';
import {
    asyncAdminWorkspaceGet,
    asyncAdminWorkspaceUpdate
} from '@he-novation/front-shared/async/admin.async';
import { bytesToGigaBytes } from '@he-novation/utils/bytes';
import cn from 'classnames';
import ModalBottomButtons from '../ModalBottomButtons/ModalBottomButtons';

import { ModalContent } from '$components/modal/ModalContent/ModalContent';
import ModalHeader from '$components/modal/ModalHeader/ModalHeader';

type ModalAdminWorkspaceUpdateProps = {
    payload: {
        workspaceName: string;
        onUpdated: (client: Client) => void;
    };
    closeModal: () => void;
};

export default function ModalAdminWorkspaceUpdate({
    payload: { workspaceName, onUpdated },
    closeModal
}: ModalAdminWorkspaceUpdateProps) {
    const [isLoading, setIsLoading] = useState(false);

    const formId = 'form-client';
    const [error, setError] = useState<string | null>(null);
    const [workspace, setWorkspace] = useState<DetailedWorkspace | null>(null);

    useEffect(() => {
        asyncAdminWorkspaceGet(workspaceName).then((workspace) => {
            setWorkspace(workspace);
        });
    }, [workspaceName]);

    const features = Object.values(WorkspaceFeature).filter(
        (f) => f !== WorkspaceFeature.DownloadOptions
    );

    return (
        <ModularForm
            id={formId}
            parseAccessors
            onSubmit={async (e, data) => {
                e.preventDefault();
                setIsLoading(true);
                try {
                    if (!Array.isArray(data.features)) data.features = [data.features];
                    if (data.storage !== -1) data.storage = data.storage * 1024 * 1024 * 1024;
                    const client = await asyncAdminWorkspaceUpdate(workspaceName, data);
                    onUpdated(client);

                    closeModal();
                } catch (e) {
                    if (e.message) setError(e.message);
                } finally {
                    setIsLoading(false);
                }
            }}
        >
            <ModalContent className={cn(formStyles.light, formStyles.block)}>
                <ModalHeader title={'Edit workspace'} />
                {error && <Error error={error} />}
                {workspace ? (
                    <>
                        <FormFields
                            formId={formId}
                            fields={[
                                {
                                    type: 'text',
                                    name: 'displayName',
                                    label: 'Name',
                                    value: workspace.displayName,
                                    minLength: 3,
                                    maxLength: 30,
                                    validation: {
                                        required: true
                                    }
                                },
                                {
                                    type: 'number',
                                    name: 'licenses',
                                    value: workspace.licenses.standardLicenses.total,
                                    coerceType: 'int',
                                    label: 'Standard licenses',
                                    className: 'size-half',
                                    validation: {
                                        required: true
                                    }
                                },
                                {
                                    type: 'number',
                                    name: 'basicLicenses',
                                    value: workspace.licenses.basicLicenses.total,
                                    coerceType: 'int',
                                    label: 'Basic licenses',
                                    className: 'size-half has-left-margin'
                                },
                                {
                                    type: 'number',
                                    name: 'translationCredits',
                                    size: 'half',
                                    coerceType: 'int',
                                    className: 'size-half',
                                    value: workspace.translationCredits,
                                    label: 'Translation credits'
                                },
                                {
                                    type: 'number',
                                    name: 'storage',
                                    size: 'half',
                                    coerceType: 'int',
                                    className: 'size-half has-left-margin',
                                    value:
                                        workspace.storage.total === -1
                                            ? -1
                                            : bytesToGigaBytes(workspace.storage.total),
                                    label: 'Storage (GB)'
                                },
                                {
                                    type: 'select',
                                    name: 'locale',
                                    size: 'half',
                                    className: 'size-half',
                                    value: workspace.locale,
                                    children: (
                                        <>
                                            <option value="fr">FR</option>
                                            <option value="en">EN</option>
                                        </>
                                    ),
                                    label: 'Locale'
                                }
                            ]}
                        />
                        <fieldset style={{ marginTop: 20 }}>
                            <legend>Features</legend>
                            <FormFields
                                formId={formId}
                                fields={features.map((feature, i) => ({
                                    type: 'checkbox',
                                    name: 'features',
                                    id: formId + '-' + feature,
                                    className:
                                        'size-half ' + (i % 2 !== 0 ? 'has-left-margin' : ''),
                                    wrapperProps: {
                                        style: {
                                            marginTop: 10
                                        }
                                    },
                                    value: feature,
                                    checked: workspace.features?.includes(feature),
                                    label: feature
                                }))}
                            />
                        </fieldset>
                    </>
                ) : (
                    <Loader />
                )}
            </ModalContent>

            <ModalBottomButtons
                buttons={[
                    {
                        children: 'Cancel',
                        onClick: closeModal,
                        tone: ButtonTone.Outlined
                    },
                    {
                        type: 'submit',
                        form: formId,
                        children: 'Save',
                        tone: ButtonTone.Primary,
                        isLoading,
                        disabled: !workspace
                    }
                ]}
            />
        </ModularForm>
    );
}
